/**
 * Monitoramento — Helpers centralizados para visitantes monitorados.
 *
 * Regra de negócio:
 *  - Um visitante é "monitorado" quando `v.monitorado === true` e possui `v.tempo_limite`.
 *
 *  Estados possíveis (avaliados em ordem):
 *    1) Visitante JÁ SAIU (`data_hora_saida` preenchida):
 *         a) Saiu DENTRO do prazo (`data_hora_saida <= tempo_limite`)
 *              → status "ok_cumprido": ícone amarelo SEM pulse.
 *                Hint: "Monitoramento cumprido — saída dentro do prazo."
 *         b) Saiu FORA do prazo (`data_hora_saida > tempo_limite`)
 *              → status "violacao": ícone vermelho PULSANTE.
 *                Hint: "Caso de estudo por violação — saída Xh Ymin após o prazo."
 *
 *    2) Visitante AINDA NÃO SAIU:
 *         a) Ainda dentro do prazo (`agora <= tempo_limite`)
 *              → status "ok": ícone amarelo SEM pulse.
 *                Hint: "Veículo monitorado — pode sair sem alertas até DD/MM/AAAA HH:MM."
 *         b) Já passou do prazo (`agora > tempo_limite`)
 *              → status "expirado": ícone vermelho PULSANTE.
 *                Hint: "Monitoramento Expirado há Xh Ymin."
 *
 * `getMonitStatus(v)` é a fonte única de verdade para renderizar o ícone em qualquer
 * relatório / lista / modal.
 *
 * Retorno:
 *  {
 *    monitorado:  bool,    // true se v.monitorado && v.tempo_limite válidos
 *    expirado:    bool,    // true em "violacao" OU "expirado" (qualquer condição vermelha)
 *    status:      string,  // "ok" | "ok_cumprido" | "expirado" | "violacao"
 *    hint:        string,  // tooltip pronto pra usar em <span title={hint}>
 *    label:       string,  // "OK" | "Expirado" | "Violação"  (uso em badges textuais)
 *    iconClass:   string,  // classes p/ wrapper do <AlertTriangle/>
 *    badgeClass:  string,  // classes p/ <span> badge (background + texto)
 *    tempoExcedido: string|null, // "3h 8min" se vermelho, senão null
 *  }
 *
 * Se `v` não for monitorado, `monitorado=false` e os demais campos voltam
 * vazios/false — basta o chamador conferir `monitorado` antes de renderizar.
 */

const HORA_MS = 3600000;
const MIN_MS = 60000;

function formatDuracao(diffMs) {
  if (diffMs < 0) diffMs = 0;
  const horas = Math.floor(diffMs / HORA_MS);
  const mins = Math.floor((diffMs % HORA_MS) / MIN_MS);
  return horas > 0 ? `${horas}h ${mins}min` : `${mins}min`;
}

function formatLimitePtBR(dt) {
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const VAZIO = {
  monitorado: false,
  expirado: false,
  status: "",
  hint: "",
  label: "",
  iconClass: "",
  badgeClass: "",
  tempoExcedido: null,
};

export function getMonitStatus(v) {
  if (!v || !v.monitorado || !v.tempo_limite) return VAZIO;

  const lim = new Date(v.tempo_limite);
  if (isNaN(lim.getTime())) {
    return {
      monitorado: true,
      expirado: false,
      status: "ok",
      hint: `Veículo monitorado — limite: ${v.tempo_limite}`,
      label: "OK",
      iconClass: "text-yellow-400",
      badgeClass: "bg-emerald-500/20 text-emerald-300",
      tempoExcedido: null,
    };
  }

  // 1) Já saiu? Avalia comparando data_hora_saida com tempo_limite.
  if (v.data_hora_saida) {
    const saida = new Date(v.data_hora_saida);
    const saidaValida = !isNaN(saida.getTime());
    if (saidaValida && saida > lim) {
      // VIOLAÇÃO — saiu após o prazo
      const tempoExcedido = formatDuracao(saida - lim);
      return {
        monitorado: true,
        expirado: true,
        status: "violacao",
        hint: `Caso de estudo por violação — saída ${tempoExcedido} após o prazo.`,
        label: "Violação",
        iconClass: "text-red-500 animate-pulse",
        badgeClass: "bg-red-500/20 text-red-300 animate-pulse",
        tempoExcedido,
      };
    }
    // Saiu dentro do prazo (ou saída inválida) → cumprido
    return {
      monitorado: true,
      expirado: false,
      status: "ok_cumprido",
      hint: "Monitoramento cumprido — saída dentro do prazo.",
      label: "OK",
      iconClass: "text-yellow-400",
      badgeClass: "bg-emerald-500/20 text-emerald-300",
      tempoExcedido: null,
    };
  }

  // 2) Ainda não saiu — compara agora com tempo_limite
  const agora = new Date();
  if (agora > lim) {
    const tempoExcedido = formatDuracao(agora - lim);
    return {
      monitorado: true,
      expirado: true,
      status: "expirado",
      hint: `Monitoramento Expirado há ${tempoExcedido}.`,
      label: "Expirado",
      iconClass: "text-red-500 animate-pulse",
      badgeClass: "bg-red-500/20 text-red-300 animate-pulse",
      tempoExcedido,
    };
  }

  // Dentro do prazo, sem saída ainda
  return {
    monitorado: true,
    expirado: false,
    status: "ok",
    hint: `Veículo monitorado — pode sair sem alertas até ${formatLimitePtBR(lim)}.`,
    label: "OK",
    iconClass: "text-yellow-400",
    badgeClass: "bg-emerald-500/20 text-emerald-300",
    tempoExcedido: null,
  };
}
